
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { EscortProfile } from '@/types';

interface EscortProfilesProps {
  escorts: EscortProfile[];
}

const EscortProfiles: React.FC<EscortProfilesProps> = ({ escorts }) => {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Escort Profiles</CardTitle>
        <CardDescription>
          Manage escort profiles, review content, and moderate listings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Escort</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead>Touring</TableHead>
              <TableHead>Services</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {escorts.map((escort) => (
              <TableRow key={escort.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={`https://via.placeholder.com/32?text=${escort.displayName.charAt(0)}`} />
                      <AvatarFallback>{escort.displayName.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">{escort.displayName}</div>
                      <div className="text-xs text-muted-foreground">Age: {escort.age}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {escort.location.city}, {escort.location.country}
                </TableCell>
                <TableCell>
                  {escort.isVerified ? (
                    <Badge className="bg-green-500">Verified</Badge>
                  ) : (
                    <Badge variant="outline">Unverified</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {escort.isTouring ? (
                    <Badge className="bg-purple-DEFAULT">
                      Touring ({escort.tourDates?.[0]?.location})
                    </Badge>
                  ) : (
                    <Badge variant="outline">Not Touring</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {escort.services.slice(0, 2).map((service, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {service.name}
                      </Badge>
                    ))}
                    {escort.services.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{escort.services.length - 2} more
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate(`/profile/${escort.id}`)}
                    >
                      View
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                    >
                      Suspend
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default EscortProfiles;
